import { ZomentumEntities } from "@/Contracts/common";
import {
	CallLogOutComeValue,
	SalesActivity,
	SalesActivityAutomation,
	SalesActivityAutomationMetadataFilterOperator,
	SalesActivityAutomationTriggerActionTypes
} from "@/Contracts/Others/SalesActivity";
import { message } from "antd";
import ZButton from "@atoms/ZButton";
import { ZPricingPlanUpgradeAlert } from "@molecules/ZPricingPlanError";
import {
	UserCompany,
	UserCompanyConstraintModules,
	UserData
} from "@/Contracts/Users";
import {
	ClientContact,
	Client,
	ClientUserContactType
} from "@/Contracts/Clients";
import { Opportunity } from "@/Contracts/Opportunity";
import { IEditOpportunityDrawerDisplayedValues } from "../components/SalesActivityAutomation/Drawers/EditOpportunityDrawer/EditOpportunityDrawer";
import ZMessage from "@molecules/ZMessage";
import { MERGETAGSREGEX } from "@/Utils/MergeTags";
import { EMergeTagStatus } from "@/Contracts/Others/MergeTags";
import { EmailIntegration } from "@/Contracts/Settings";
import {
	RelatedClientInformation,
	VendorClientContact,
	VendorUserCompany,
	VendorUserData
} from "@/Contracts";

export interface CheckAutomationExhaustParams {
	readonly salesAutomationConstraintModule?: UserCompanyConstraintModules;
	readonly isInternal: boolean;
	readonly redirect: () => void;
}

export const checkForPaymentRecievedEmail = (
	automation: SalesActivityAutomation
) => {
	if (
		automation.trigger_entity_type === ZomentumEntities.Document &&
		automation.trigger_action_type ===
			SalesActivityAutomationTriggerActionTypes.Updated
	) {
		if (automation?.filter_groups?.length === 1) {
			const firstGroup = automation?.filter_groups[0];
			if (
				firstGroup.length === 1 &&
				firstGroup[0]?.fields_and_values.length === 1
			) {
				const filter_criteria = firstGroup[0]?.fields_and_values[0];
				return (
					filter_criteria.field_name === "payment.status" &&
					filter_criteria.filter_operator?.id_value ===
						SalesActivityAutomationMetadataFilterOperator.HasChangedTo &&
					filter_criteria.values?.[0]?.id_value ===
						"Payment completed"
				);
			}
		}
	}
	return false;
};

export const checkForAutomationExhaustion = (
	params: CheckAutomationExhaustParams
) => {
	const closeError = () => message.destroy("automations_exhausted");
	const onUpgradeNowClick = () => {
		closeError();
		params.redirect();
	};

	const areAutomationsExhausted =
		!(
			params.salesAutomationConstraintModule?.module_usage_count
				.available ?? true
		) && !params.isInternal;

	if (areAutomationsExhausted) {
		message.open({
			type: "error",
			onClose: closeError,
			key: "automations_exhausted",
			duration: 5,
			content: (
				<ZPricingPlanUpgradeAlert
					title="Upgrade to a higher plan to get access"
					description="You have reached the maximun number of automations available in your billing plan"
					textAlignmentLeft
					footer={
						<div className="flex items-center justify-end">
							<div>
								<ZButton
									type="link"
									title="Skip"
									onClick={closeError}
								>
									Skip
								</ZButton>
							</div>
							<div>
								<ZButton
									type="primary"
									title="Upgrade Now"
									onClick={onUpgradeNowClick}
								>
									Upgrade Now
								</ZButton>
							</div>
						</div>
					}
				/>
			),
			className: "zMessage mt-14"
		});

		return false;
	}

	return true;
};

export const getTaskContact = (
	relation: ZomentumEntities | null,
	entityId: string | null,
	clientContacts: ClientContact[] | VendorClientContact[] | null,
	opportunities: Opportunity[] | null,
	clients: Client[] | null
): ClientContact | VendorClientContact | null => {
	if (relation) {
		if (relation === ZomentumEntities.Opportunity) {
			const opportunityId = entityId;
			if (opportunityId && opportunityId?.length > 0) {
				const selectedOpportunity = opportunities?.find(
					opportunity => opportunity.id === opportunityId
				);
				if (selectedOpportunity) {
					return selectedOpportunity?.primary_client_user ?? null;
				}
			}
		} else if (relation === ZomentumEntities.Client) {
			const clientId = entityId;
			if (clientId && clientId?.length > 0) {
				const selectedClient = clients?.find(
					client => client.id === clientId
				);
				if (
					selectedClient &&
					clientContacts &&
					clientContacts?.length > 0
				) {
					const primaryContact = (
						clientContacts as ClientContact[]
					)?.find(
						clientContact =>
							clientContact.contact_type ===
							ClientUserContactType.PrimaryContact
					);
					if (primaryContact) {
						return primaryContact;
					} else {
						const sortedContacts = clientContacts?.sort(
							(a, b) =>
								((b?.updated &&
									new Date(b.updated)?.getTime()) ||
									0) -
								((a?.updated &&
									new Date(a.updated)?.getTime()) ||
									0)
						);
						if (sortedContacts && sortedContacts?.length > 0) {
							return sortedContacts[0];
						}
					}
				}
			}
		}
	}
	return null;
};

export const sanitizeTextRegex = (): Record<string, RegExp> => {
	return {
		attendeesRegExp:
			/(attendees[.]attendee_name|attendees\[\d\][.]attendee_name)/gm,
		idsRegExp:
			/(_ids|_id|[.][$]numberLong|[.]id|[.]string_value|\[\d+\])/gm,
		customFieldsRegExp: /Custom\sfields\s\S+/
	};
};

export const opportunityRevenueAndCostFieldsList = [
	"use_document_totals",
	"months_to_calculate_margin",
	"manual_total"
];

export const getOpportunityFields = (
	salesAutomationEditOppClientFieldsFlag: boolean
): IEditOpportunityDrawerDisplayedValues[] => {
	const opportunityFields: IEditOpportunityDrawerDisplayedValues[] = [
		{
			display_value: "Title",
			id_value: "name"
		}
	];
	if (salesAutomationEditOppClientFieldsFlag)
		opportunityFields.push({
			display_value: "Client",
			id_value: "client_company_id"
		});
	opportunityFields.push(
		{
			display_value: "Opportunity Type",
			id_value: "opportunity_type"
		},
		{
			display_value: "Stage",
			id_value: "opportunity_pipeline_stage_id"
		},
		{
			display_value: "Owner",
			id_value: "owner_user_id"
		}
	);
	if (salesAutomationEditOppClientFieldsFlag)
		opportunityFields.push(
			{
				display_value: "Opportunity Primary Contact",
				id_value: "primary_client_user_id"
			},
			{
				display_value: "Linked Contacts",
				id_value: "linked_client_user_ids"
			}
		);
	opportunityFields.push(
		{
			display_value: "Expected Close Date",
			id_value: "estimated_close_date"
		},
		{
			display_value: "Client PO Number",
			id_value: "client_po_number"
		},
		{
			display_value: "Close Probability",
			id_value: "close_probability"
		},
		{
			display_value: "Estimated Value",
			id_value: "estimated_value"
		},
		{
			display_value: "Campaign",
			id_value: "campaign"
		},
		{
			display_value: "Source",
			id_value: "source"
		},
		{
			display_value: "Revenue Fields",
			id_value: "revenue_fields"
		}
	);
	return opportunityFields;
};

export const validateDisplayedFieldValues = (
	opportunityFields: IEditOpportunityDrawerDisplayedValues[],
	field_value: string
) => {
	return !!opportunityFields.find(f => f.id_value === field_value);
};

export const detectMergeTags = (text: string) => {
	const MergeTags = text.match(MERGETAGSREGEX);
	return MergeTags;
};

export const validateNumericAndDecimalData = (
	data: string,
	fieldName: string
) => {
	if (data.length) {
		data = data.replace(/\s+/g, "");
		const MergeTags = detectMergeTags(data);
		if (MergeTags?.length) {
			if (MergeTags.length === 1) {
				if (data.length - MergeTags[0].length) {
					ZMessage.error(
						`Invalid entry in “${fieldName}” field. Enter either a merge tag or numeric value`
					);
					return EMergeTagStatus.ONEMERGETAGWITHNUMBER;
				} else return EMergeTagStatus.SUCCESS;
			} else {
				ZMessage.error(
					`Invalid entry in “${fieldName}” field. Only one merge tag is allowed`
				);
				return EMergeTagStatus.TWOMERGETAGS;
			}
		}
	}
	return EMergeTagStatus.SUCCESS;
};

export const getCurrentUserEmailIntegration = (
	userCompany: UserCompany | VendorUserCompany,
	userData: UserData | VendorUserData
): EmailIntegration | undefined => {
	const emailIntegration = (
		userCompany as UserCompany
	)?.integrations?.email?.find(e => e.user_id === userData.id);

	return emailIntegration;
};

export const defaultCallLogOptions = [
	{
		name: CallLogOutComeValue.AskedToCallLater,
		value: CallLogOutComeValue.AskedToCallLater
	},
	{
		name: CallLogOutComeValue.Busy,
		value: CallLogOutComeValue.Busy
	},
	{
		name: CallLogOutComeValue.WrongNumber,
		value: CallLogOutComeValue.WrongNumber
	},
	{
		name: CallLogOutComeValue.LeftVoicemail,
		value: CallLogOutComeValue.LeftVoicemail
	},
	{
		name: CallLogOutComeValue.Connected,
		value: CallLogOutComeValue.Connected
	},
	{
		name: CallLogOutComeValue.Interested,
		value: CallLogOutComeValue.Interested
	}
];

export const getEntityListForAssignedAccess = (
	salesActivity: SalesActivity | null,
	entity: ZomentumEntities,
	isEntityAssigned: boolean
): Client[] | Opportunity[] => {
	if (!salesActivity) {
		return [];
	}
	const { entity_obj } = salesActivity.feed_details;
	const result =
		isEntityAssigned &&
		!(entity_obj as RelatedClientInformation)?.is_demo_client
			? []
			: [
					{
						name: entity_obj?.name,
						id: entity_obj?.id
					}
			  ];
	switch (entity) {
		case ZomentumEntities.Client:
			return result as Client[];
		case ZomentumEntities.Opportunity:
			return result as Opportunity[];
		default:
			return [];
	}
};
export interface IDoughnutChartWithTextLabelFontOptions {
	readonly size: string;
	readonly family: string;
}
export interface IDoughnutChartWithTextLabelOptions {
	readonly text: string;
	readonly font: IDoughnutChartWithTextLabelFontOptions;
	readonly color: string;
}
